/**
 * FAQPAGE.JS v3 - WEISSES DESIGN + PLANIVIO TEAL
 * 
 * ✅ v3: Weißes Design + Planivio Teal/Green
 *        - Weißer Hintergrund
 *        - Teal/Green Farbschema
 *        - Echtes Logo
 * ✅ v2: i18n Support
 * ✅ v1: Akkordeon-Design, Kategorien, Suche
 * 
 * @version 3.0.0 - Weißes Design + Planivio Teal
 */

import React, { useState, useMemo } from 'react';
import { useTranslation, LANGUAGES } from '../context/I18nContext';
import { usePublicPageView } from '../hooks/useAnalytics';

// ============================================================================
// PLANIVIO FARBEN
// ============================================================================
const PLANIVIO = {
  teal: '#4A90A4',
  green: '#6BA893',
  gradient: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
  shadow: 'rgba(74, 144, 164, 0.3)'
};

const COLORS = {
  bgWhite: '#FFFFFF',
  bgLight: '#F8FAFC',
  bgSection: '#F1F5F9',
  textDark: '#1E293B',
  textMedium: '#475569',
  textLight: '#64748B',
  textMuted: '#94A3B8',
  border: '#E2E8F0',
  accent: PLANIVIO.teal,
  accentGreen: PLANIVIO.green
};

function FAQPage({ onNavigate }) {
  usePublicPageView('faq');
  const { t, language, setLanguage, currentLanguage } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const categories = [
    { id: 'all', label: t('faq.categories.all'), icon: '📋' },
    { id: 'general', label: t('faq.categories.general'), icon: '💡' },
    { id: 'beta', label: t('faq.categories.beta'), icon: '🎉' },
    { id: 'features', label: t('faq.categories.features'), icon: '⚙️' },
    { id: 'privacy', label: t('faq.categories.privacy'), icon: '🔒' },
    { id: 'technical', label: t('faq.categories.technical'), icon: '🔧' }
  ];

  // Build FAQs from translations
  const faqs = useMemo(() => [
    // ===== GENERAL =====
    { category: 'general', question: t('faq.general.whatIsHomeM.q'), answer: t('faq.general.whatIsHomeM.a') },
    { category: 'general', question: t('faq.general.forWhom.q'), answer: t('faq.general.forWhom.a') },
    { category: 'general', question: t('faq.general.isFree.q'), answer: t('faq.general.isFree.a') },
    { category: 'general', question: t('faq.general.devices.q'), answer: t('faq.general.devices.a') },

    // ===== BETA =====
    { category: 'beta', question: t('faq.beta.whatIsBeta.q'), answer: t('faq.beta.whatIsBeta.a') },
    { category: 'beta', question: t('faq.beta.howLong.q'), answer: t('faq.beta.howLong.a') },
    { category: 'beta', question: t('faq.beta.benefits.q'), answer: t('faq.beta.benefits.a') },
    { category: 'beta', question: t('faq.beta.feedback.q'), answer: t('faq.beta.feedback.a') },
    { category: 'beta', question: t('faq.beta.dataAfter.q'), answer: t('faq.beta.dataAfter.a') },

    // ===== FEATURES =====
    { category: 'features', question: t('faq.features.modules.q'), answer: t('faq.features.modules.a') },
    { category: 'features', question: t('faq.features.aiAssistant.q'), answer: t('faq.features.aiAssistant.a') },
    { category: 'features', question: t('faq.features.multiUser.q'), answer: t('faq.features.multiUser.a') },
    { category: 'features', question: t('faq.features.recipeToShopping.q'), answer: t('faq.features.recipeToShopping.a') },
    { category: 'features', question: t('faq.features.offline.q'), answer: t('faq.features.offline.a') },

    // ===== PRIVACY =====
    { category: 'privacy', question: t('faq.privacy.dataStorage.q'), answer: t('faq.privacy.dataStorage.a') },
    { category: 'privacy', question: t('faq.privacy.dataSold.q'), answer: t('faq.privacy.dataSold.a') },
    { category: 'privacy', question: t('faq.privacy.cookies.q'), answer: t('faq.privacy.cookies.a') },
    { category: 'privacy', question: t('faq.privacy.export.q'), answer: t('faq.privacy.export.a') },
    { category: 'privacy', question: t('faq.privacy.deleteAccount.q'), answer: t('faq.privacy.deleteAccount.a') },

    // ===== TECHNICAL =====
    { category: 'technical', question: t('faq.technical.browsers.q'), answer: t('faq.technical.browsers.a') },
    { category: 'technical', question: t('faq.technical.installMobile.q'), answer: t('faq.technical.installMobile.a') },
    { category: 'technical', question: t('faq.technical.forgotPassword.q'), answer: t('faq.technical.forgotPassword.a') },
    { category: 'technical', question: t('faq.technical.notLoading.q'), answer: t('faq.technical.notLoading.a') },
    { category: 'technical', question: t('faq.technical.nativeApp.q'), answer: t('faq.technical.nativeApp.a') }
  ], [t]);

  // Filter FAQs
  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div style={{ background: COLORS.bgWhite, minHeight: '100vh', color: COLORS.textDark }}>
      
      {/* Responsive Styles */}
      <style>{`
        .faq-container {
          max-width: 900px;
          margin: 0 auto;
          padding: 60px 40px;
        }
        
        .faq-search {
          width: 100%;
          padding: 16px 20px 16px 50px;
          border-radius: 12px;
          border: 2px solid ${COLORS.border};
          background: ${COLORS.bgLight};
          color: ${COLORS.textDark};
          font-size: 1rem;
          outline: none;
          transition: border-color 0.2s;
        }
        
        .faq-search:focus {
          border-color: ${COLORS.accent};
        }
        
        .faq-search::placeholder {
          color: ${COLORS.textMuted};
        }
        
        .faq-categories {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          margin-bottom: 30px;
        }
        
        .faq-category-btn {
          padding: 10px 18px;
          border-radius: 25px;
          border: 1px solid ${COLORS.border};
          background: ${COLORS.bgWhite};
          color: ${COLORS.textMedium};
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .faq-category-btn:hover {
          background: ${COLORS.bgLight};
          color: ${COLORS.textDark};
          border-color: ${COLORS.accent};
        }
        
        .faq-category-btn.active {
          background: ${PLANIVIO.gradient};
          border-color: transparent;
          color: white;
        }
        
        .faq-item {
          margin-bottom: 12px;
          border-radius: 12px;
          border: 1px solid ${COLORS.border};
          background: ${COLORS.bgWhite};
          overflow: hidden;
          transition: all 0.2s;
        }
        
        .faq-item:hover {
          border-color: ${COLORS.accent};
          box-shadow: 0 4px 15px ${PLANIVIO.shadow};
        }
        
        .faq-item.open {
          border-color: ${COLORS.accent};
          background: ${COLORS.bgLight};
        }
        
        .faq-question {
          padding: 20px 24px;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        }
        
        .faq-question-text {
          font-size: 1.05rem;
          font-weight: 600;
          color: ${COLORS.textDark};
          flex: 1;
        }
        
        .faq-arrow {
          font-size: 1.2rem;
          color: ${COLORS.accent};
          transition: transform 0.3s;
        }
        
        .faq-arrow.open {
          transform: rotate(180deg);
        }
        
        .faq-answer {
          padding: 0 24px 20px;
          color: ${COLORS.textMedium};
          font-size: 0.95rem;
          line-height: 1.7;
        }
        
        .faq-no-results {
          text-align: center;
          padding: 60px 20px;
          color: ${COLORS.textMuted};
        }
        
        @media (max-width: 768px) {
          .faq-container {
            padding: 40px 20px;
          }
          
          .faq-categories {
            justify-content: center;
          }
          
          .faq-category-btn {
            padding: 8px 14px;
            font-size: 0.85rem;
          }
          
          .faq-question {
            padding: 16px 18px;
          }
          
          .faq-question-text {
            font-size: 0.95rem;
          }
          
          .faq-answer {
            padding: 0 18px 16px;
            font-size: 0.9rem;
          }
        }
      `}</style>

      {/* Navigation */}
      <nav style={{
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        background: COLORS.bgWhite,
        backdropFilter: 'blur(10px)',
        zIndex: 1000,
        borderBottom: `1px solid ${COLORS.border}`,
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
      }}>
        <div 
          onClick={() => onNavigate && onNavigate('landing')}
          style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
        >
          {/* ✅ v3: Echtes Planivio Logo */}
          <img 
            src="/images/Logos/planivio_logo_primary_400px.png" 
            alt="Planivio" 
            style={{ height: '36px', width: 'auto' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div style={{ display: 'none', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '2rem' }}>📋</span>
            <span style={{ fontSize: '1.5rem', fontWeight: 800, color: COLORS.accent }}>planivio</span>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Language Switcher Button */}
          <button
            onClick={() => setShowLanguageMenu(!showLanguageMenu)}
            style={{
              padding: '8px 14px',
              background: COLORS.bgLight,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.textMedium,
              cursor: 'pointer',
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
          >
            <span>{currentLanguage.flag}</span>
            <span>{currentLanguage.code.toUpperCase()}</span>
            <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>▼</span>
          </button>
          
          <button
            onClick={() => onNavigate && onNavigate('landing')}
            style={{
              padding: '10px 24px',
              background: COLORS.bgLight,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '10px',
              color: COLORS.textMedium,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            ← {t('common.back')}
          </button>
        </div>
      </nav>

      {/* Content */}
      <div className="faq-container">
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
          <h1 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 800, 
            marginBottom: '16px',
            background: PLANIVIO.gradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            {t('faq.title')}
          </h1>
          <p style={{ color: COLORS.textLight, fontSize: '1.1rem' }}>
            {t('faq.subtitle')}
          </p>
        </div>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: '30px' }}>
          <span style={{ 
            position: 'absolute', 
            left: '18px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            fontSize: '1.2rem'
          }}>
            🔍
          </span>
          <input
            type="text"
            className="faq-search"
            placeholder={t('faq.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Categories */}
        <div className="faq-categories">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`faq-category-btn ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              <span>{cat.icon}</span>
              <span>{cat.label}</span>
            </button>
          ))}
        </div>

        {/* FAQ List */}
        {filteredFaqs.length > 0 ? (
          <div>
            {filteredFaqs.map((faq, index) => (
              <div 
                key={index} 
                className={`faq-item ${openIndex === index ? 'open' : ''}`}
              >
                <div 
                  className="faq-question"
                  onClick={() => toggleFaq(index)}
                >
                  <span className="faq-question-text">{faq.question}</span>
                  <span className={`faq-arrow ${openIndex === index ? 'open' : ''}`}>
                    ▼
                  </span>
                </div>
                {openIndex === index && (
                  <div className="faq-answer">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="faq-no-results">
            <span style={{ fontSize: '3rem', display: 'block', marginBottom: '16px' }}>🔍</span>
            <p style={{ fontSize: '1.1rem', marginBottom: '8px' }}>{t('faq.noResults')}</p>
            <p>{t('faq.tryOther')}</p>
          </div>
        )}

        {/* Contact CTA */}
        <div style={{
          marginTop: '60px',
          padding: '30px',
          background: COLORS.bgLight,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '16px',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '12px', color: COLORS.textDark }}>
            {t('faq.contactTitle')}
          </h3>
          <p style={{ color: COLORS.textMedium, marginBottom: '20px' }}>
            {t('faq.contactSubtitle')}
          </p>
          <a
            href="mailto:kontakt@planivio.de"
            style={{
              display: 'inline-block',
              padding: '14px 28px',
              background: PLANIVIO.gradient,
              borderRadius: '10px',
              color: 'white',
              fontSize: '1rem',
              fontWeight: 600,
              textDecoration: 'none',
              boxShadow: `0 4px 15px ${PLANIVIO.shadow}`
            }}
          >
            📧 kontakt@planivio.de
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        padding: '30px 40px',
        borderTop: `1px solid ${COLORS.border}`,
        background: COLORS.bgLight,
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', gap: '25px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <span 
            onClick={() => onNavigate && onNavigate('datenschutz')}
            style={{ color: COLORS.textMedium, fontSize: '0.9rem', cursor: 'pointer' }}
          >
            {t('landing.footer.privacy')}
          </span>
          <span 
            onClick={() => onNavigate && onNavigate('agb')}
            style={{ color: COLORS.textMedium, fontSize: '0.9rem', cursor: 'pointer' }}
          >
            {t('landing.footer.terms')}
          </span>
          <span 
            onClick={() => onNavigate && onNavigate('impressum')}
            style={{ color: COLORS.textMedium, fontSize: '0.9rem', cursor: 'pointer' }}
          >
            {t('landing.footer.imprint')}
          </span>
          <span style={{ color: COLORS.accent, fontSize: '0.9rem', fontWeight: 600 }}>
            FAQ
          </span>
        </div>
        <div style={{ marginTop: '20px', color: COLORS.textMuted, fontSize: '0.8rem' }}>
          © 2026 Planivio. {t('landing.footer.rights')} 🇩🇪
        </div>
      </footer>

      {/* Language Dropdown */}
      {showLanguageMenu && (
        <>
          <div 
            onClick={() => setShowLanguageMenu(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 99998
            }}
          />
          <div style={{
            position: 'fixed',
            top: '70px',
            right: '20px',
            background: COLORS.bgWhite,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '12px',
            padding: '8px',
            minWidth: '160px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            zIndex: 99999
          }}>
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setShowLanguageMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: language === lang.code 
                    ? PLANIVIO.gradient 
                    : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: language === lang.code ? 'white' : COLORS.textDark,
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s',
                  textAlign: 'left',
                  marginBottom: '4px'
                }}
                onMouseEnter={(e) => {
                  if (language !== lang.code) {
                    e.target.style.background = COLORS.bgLight;
                  }
                }}
                onMouseLeave={(e) => {
                  if (language !== lang.code) {
                    e.target.style.background = 'transparent';
                  }
                }}
              >
                <span style={{ fontSize: '1.3rem' }}>{lang.flag}</span>
                <span>{lang.name}</span>
                {language === lang.code && (
                  <span style={{ marginLeft: 'auto', opacity: 0.8 }}>✓</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default FAQPage;